import React, { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Plus,
  Edit,
  Trash2,
  Layers,
  X
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useEngineMaturityLevels, useCreateEngineMaturityLevel, useUpdateEngineMaturityLevel, useDeleteEngineMaturityLevel } from '@/features/maturity-levels/hooks'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'

interface MaturityLevel {
  id: string
  level: number
  name: string
  description: string
  color: string
  characteristics: string[]
  capabilities: string[]
  challenges: string[]
  created_at: string
  updated_at: string
}

interface MaturityLevelFormData {
  level: number
  name: string
  description: string
  color: string
  characteristics: string[]
  capabilities: string[]
  challenges: string[]
}

// ── Reusable tag-input for list fields ──────────────────────────────────────

interface TagInputProps {
  label: string
  placeholder: string
  items: string[]
  onAdd: (value: string) => void
  onRemove: (index: number) => void
}

function TagInput({ label, placeholder, items, onAdd, onRemove }: TagInputProps) {
  const [value, setValue] = React.useState('')

  const handleAdd = () => {
    if (value.trim()) {
      onAdd(value.trim())
      setValue('')
    }
  }

  return (
    <div>
      <Label>{label}</Label>
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAdd()
              }
            }}
          />
          <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
            Add
          </Button>
        </div>
        {items.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {items.map((item, index) => (
              <Badge key={index} variant="secondary" className="gap-1 pr-1">
                {item}
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Color presets ───────────────────────────────────────────────────────────

const COLOR_PRESETS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6',
  '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1', '#64748b',
]

// ── Shared form fields component ────────────────────────────────────────────

interface MaturityFormFieldsProps {
  formData: MaturityLevelFormData
  setFormData: React.Dispatch<React.SetStateAction<MaturityLevelFormData>>
  isEdit?: boolean
}

function MaturityFormFields({ formData, setFormData, isEdit = false }: MaturityFormFieldsProps) {
  const addItem = (type: 'characteristics' | 'capabilities' | 'challenges', value: string) => {
    setFormData(prev => ({ ...prev, [type]: [...prev[type], value] }))
  }
  const removeItem = (type: 'characteristics' | 'capabilities' | 'challenges', index: number) => {
    setFormData(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== index) }))
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`${isEdit ? 'edit-' : ''}level`}>Level Number</Label>
          <Input
            id={`${isEdit ? 'edit-' : ''}level`}
            type="number"
            min="1"
            max="10"
            value={formData.level}
            onChange={(e) => setFormData(prev => ({ ...prev, level: parseInt(e.target.value) }))}
          />
        </div>
        <div>
          <Label htmlFor={`${isEdit ? 'edit-' : ''}name`}>Level Name</Label>
          <Input
            id={`${isEdit ? 'edit-' : ''}name`}
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Initial/Ad-hoc"
          />
        </div>
      </div>

      <div>
        <Label htmlFor={`${isEdit ? 'edit-' : ''}description`}>Description</Label>
        <Textarea
          id={`${isEdit ? 'edit-' : ''}description`}
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe this maturity level..."
          rows={3}
        />
      </div>



      {/* Color Picker */}
      <div>
        <Label>Level Color</Label>
        <div className="flex items-center gap-3 mt-1">
          <div className="flex gap-1.5">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                className="w-7 h-7 rounded-full border-2 transition-all hover:scale-110"
                style={{
                  backgroundColor: c,
                  borderColor: formData.color === c ? '#000' : 'transparent',
                  boxShadow: formData.color === c ? '0 0 0 2px white, 0 0 0 4px ' + c : 'none',
                }}
              />
            ))}
          </div>
          <Input
            type="color"
            value={formData.color}
            onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
            className="w-10 h-8 p-0.5 cursor-pointer"
          />
          <span className="text-xs text-muted-foreground font-mono">{formData.color}</span>
        </div>
      </div>

      {/* Tag Inputs */}
      <TagInput
        label="Characteristics"
        placeholder="Add characteristic..."
        items={formData.characteristics}
        onAdd={(v) => addItem('characteristics', v)}
        onRemove={(i) => removeItem('characteristics', i)}
      />

      <TagInput
        label="Capabilities"
        placeholder="Add capability..."
        items={formData.capabilities}
        onAdd={(v) => addItem('capabilities', v)}
        onRemove={(i) => removeItem('capabilities', i)}
      />

      <TagInput
        label="Challenges"
        placeholder="Add challenge..."
        items={formData.challenges}
        onAdd={(v) => addItem('challenges', v)}
        onRemove={(i) => removeItem('challenges', i)}
      />
    </div>
  )
}

// ── Main Component ──────────────────────────────────────────────────────────

const MaturityLevelsManagement: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingLevel, setEditingLevel] = useState<MaturityLevel | null>(null)
  const [formData, setFormData] = useState<MaturityLevelFormData>({
    level: 1,
    name: '',
    description: '',
    color: '#6366f1',
    characteristics: [],
    capabilities: [],
    challenges: []
  })

  const { toast } = useToast()

  // Fetch maturity levels via hook
  const { data: maturityLevelsRaw, isLoading } = useEngineMaturityLevels()
  const maturityLevels = (maturityLevelsRaw?.data ?? maturityLevelsRaw) as MaturityLevel[] | undefined

  // Mutation hooks
  const createMutationHook = useCreateEngineMaturityLevel()
  const updateMutationHook = useUpdateEngineMaturityLevel()
  const deleteMutationHook = useDeleteEngineMaturityLevel()

  const listState = useListState({ defaultPerPage: 50 })

  // Client-side search filtering
  const filteredLevels = useMemo(() => {
    if (!maturityLevels) return []
    const term = listState.search.toLowerCase()
    if (!term) return maturityLevels
    return maturityLevels.filter((ml: MaturityLevel) =>
      ml.name.toLowerCase().includes(term) ||
      ml.description?.toLowerCase().includes(term)
    )
  }, [maturityLevels, listState.search])

  const resetForm = () => {
    setFormData({
      level: 1,
      name: '',
      description: '',
      color: '#6366f1',
      characteristics: [],
      capabilities: [],
      challenges: []
    })
  }

  const handleEdit = (level: MaturityLevel) => {
    setEditingLevel(level)
    setFormData({
      level: level.level,
      name: level.name,
      description: level.description,
      color: level.color ?? '#6366f1',
      characteristics: level.characteristics ?? [],
      capabilities: level.capabilities ?? [],
      challenges: level.challenges ?? []
    })
  }

  const handleSubmit = () => {
    if (editingLevel) {
      updateMutationHook.mutate({ level: editingLevel.level, data: formData }, {
        onSuccess: () => {
          toast({ title: 'Success', description: 'Maturity level updated successfully.' })
          setEditingLevel(null)
          resetForm()
        },
        onError: (error: any) => {
          toast({ title: 'Error', description: error.response?.data?.error || 'Failed to update maturity level.', variant: 'destructive' })
        }
      })
    } else {
      createMutationHook.mutate(formData, {
        onSuccess: () => {
          toast({ title: 'Success', description: 'Maturity level created successfully.' })
          setIsCreateDialogOpen(false)
          resetForm()
        },
        onError: (error: any) => {
          toast({ title: 'Error', description: error.response?.data?.error || 'Failed to create maturity level.', variant: 'destructive' })
        }
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Layers className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p>Loading maturity levels...</p>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Maturity Levels Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage IT maturity levels and their characteristics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AuthorizationGate actions={['export_maturity_levels', 'import_maturity_levels']} mode="any">
            <ImportExportButtons
              entityType="maturity_levels"
              entityLabel="Maturity Levels"
            />
          </AuthorizationGate>
          <AuthorizationGate actions={['create_maturity_level']}>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Maturity Level
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl flex flex-col max-h-[80vh]">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle>Create New Maturity Level</DialogTitle>
                  <DialogDescription>
                    Define a new IT maturity level with its characteristics, capabilities, and challenges.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto pr-2">
                  <MaturityFormFields formData={formData} setFormData={setFormData} />
                </div>
                <DialogFooter className="flex-shrink-0 border-t pt-4">
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={createMutationHook.isPending || !formData.name || !formData.description}
                  >
                    {createMutationHook.isPending ? 'Creating...' : 'Create Level'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </AuthorizationGate>
        </div>
      </div>

      {/* Search Toolbar */}
      <DataToolbar
        listState={listState}
        searchPlaceholder="Search maturity levels..."
        totalItems={filteredLevels.length}
        showPerPage={false}
      />

      {/* Maturity Levels List */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        initial="hidden"
        animate="show"
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
      >
        {filteredLevels?.map((level: MaturityLevel) => (
          <motion.div
            key={level.id}
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.3 }}
          >
            <Card
              className="overflow-hidden"
              style={{ borderLeft: `4px solid ${level.color || '#6366f1'}` }}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: level.color || '#6366f1' }}
                    />
                    <div>
                      <CardTitle className="text-lg">{level.name}</CardTitle>
                      <CardDescription>Level {level.level}</CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <AuthorizationGate actions={['update_maturity_level']}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(level)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </AuthorizationGate>
                    <AuthorizationGate actions={['delete_maturity_level']}>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Maturity Level</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete Level {level.level}: {level.name}?
                              This action cannot be undone and may affect project initiatives that reference this level.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutationHook.mutate(level.level, {
                                onSuccess: () => toast({ title: 'Success', description: 'Maturity level deleted successfully.' }),
                                onError: (error: any) => toast({ title: 'Error', description: error.response?.data?.error || 'Failed to delete maturity level.', variant: 'destructive' })
                              })}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </AuthorizationGate>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{level.description}</p>



                <div>
                  <h4 className="text-sm font-medium mb-1">Characteristics</h4>
                  <div className="flex flex-wrap gap-1">
                    {level.characteristics.slice(0, 2).map((char, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {char}
                      </Badge>
                    ))}
                    {level.characteristics.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{level.characteristics.length - 2} more
                      </Badge>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-1">Capabilities</h4>
                  <div className="flex flex-wrap gap-1">
                    {level.capabilities.slice(0, 2).map((cap, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {cap}
                      </Badge>
                    ))}
                    {level.capabilities.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{level.capabilities.length - 2} more
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>


      {/* Edit Dialog */}
      <Dialog open={!!editingLevel} onOpenChange={() => setEditingLevel(null)}>
        <DialogContent className="max-w-2xl flex flex-col max-h-[80vh]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Edit Maturity Level</DialogTitle>
            <DialogDescription>
              Update the maturity level details and characteristics.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <MaturityFormFields formData={formData} setFormData={setFormData} isEdit />
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setEditingLevel(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={updateMutationHook.isPending || !formData.name || !formData.description}
            >
              {updateMutationHook.isPending ? 'Updating...' : 'Update Level'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

export default MaturityLevelsManagement
